import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  useCallback,
  cloneElement
} from 'react';
import type { KeyboardEvent, PropsWithoutRef, ReactNode, RefObject } from 'react';

import { getActiveElement, withTestIds, isValidElement, hasProp } from '../../utils';
import Icon, { registerIcon } from '../Icon';
import type { RefElement } from '../../types';
import {
  useConsolidatedRef,
  useElement,
  useEscape,
  useFocusTrap,
  useFocusWithin,
  useI18n,
  usePrevious,
  useTestIds,
  useUID
} from '../../hooks';
import Grid from '../Grid';
import type { GridProps } from '../Grid';
import Flex from '../Flex';
import FieldValueList from '../FieldValueList';
import type { FieldValueListItemProps } from '../FieldValueList';
import { StyledImage } from '../Image';
import * as pictureIcon from '../Icon/icons/picture.icon';
import ShowMoreLess from '../ShowMoreLess';
import Tooltip from '../Tooltip';

import { getSelectionCardTestIds } from './SelectionCard.test-ids';
import {
  StyledSelectionCard,
  StyledContentContainer,
  StyledImageContainer,
  StyledSelectionCardInline,
  StyledSelectionCardStacked,
  StyledValueList,
  StyledHeader,
  StyledImageCell,
  StyledContentCell,
  StyledTileContent,
  StyledCardFooter,
  StyledSingleField
} from './SelectionCard.styles';
import type {
  FieldValueItemPropsOmitName,
  ImagePlacement,
  ImageSize,
  SelectionCardProps,
  SelectionCardTile
} from './SelectionCard.types';
import SelectionCardActions from './SelectionCardActions';

registerIcon(pictureIcon);

const isTileContent = (
  img: NonNullable<SelectionCardProps['image']>
): img is SelectionCardTile & { placement?: ImagePlacement; size?: ImageSize } => {
  return 'foregroundColor' in img;
};

export const focusableSelector =
  'a[href], button, input, textarea, select, details, video[controls], audio[controls]';

const SingleField = ({
  id,
  'data-testid': testId,
  truncate,
  value
}: {
  id: string;
  'data-testid'?: string;
  truncate?: boolean;
  value: ReactNode;
}) => {
  const [el, setEl] = useElement<HTMLDivElement>(null);
  return (
    <>
      <StyledSingleField ref={setEl} id={id} data-testid={testId} truncate={truncate}>
        {value}
      </StyledSingleField>
      {truncate && el && (
        <Tooltip target={el} smart>
          {value}
        </Tooltip>
      )}
    </>
  );
};

const useContentHeight = (
  contentRef: RefObject<HTMLElement>,
  targetRef: RefObject<HTMLElement>
) => {
  useLayoutEffect(() => {
    if (!contentRef.current || !targetRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        targetRef.current?.style.setProperty(
          '--content-height',
          `${contentRef.current?.scrollHeight}px`
        );
      });
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);
};

const SelectionCard = forwardRef<
  RefElement<SelectionCardProps>,
  PropsWithoutRef<SelectionCardProps>
>(function SelectionCard(props, ref) {
  const uid = useUID();
  const {
    testId,
    id = uid,
    'aria-label': label,
    heading,
    disabled = false,
    readOnly,
    image,
    footer,
    fields = [],
    onClick,
    onKeyDown,
    actions,
    additionalInfo,
    ...restProps
  } = props;

  const t = useI18n();

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useConsolidatedRef(ref);

  const [focusOnInput, setFocusOnInput] = useState(false);
  const [hasInteractiveElements, setHasInteractiveElements] = useState(0);
  const [isBrokenImage, setIsBrokenImage] = useState(false);
  const isTile = image !== undefined && isTileContent(image);
  const tile = isTile ? image : undefined;
  const resolvedImageSrc = !isTile ? image?.src : undefined;
  const prevImageSrc = usePrevious(resolvedImageSrc);
  const testIds = useTestIds(testId, getSelectionCardTestIds);
  const contentId = `${id}-content`;

  const hasAdditionalContent = fields.length > 0;

  useEffect(() => {
    const focusables = containerRef.current?.querySelectorAll(focusableSelector);
    setHasInteractiveElements(focusables?.length || 0);
    setFocusOnInput(focusables?.length === 1 && focusables?.[0] instanceof HTMLInputElement);
    focusables?.forEach(el =>
      el.hasAttribute('data-main-focus')
        ? el.removeAttribute('tabindex')
        : el.setAttribute('tabindex', '-1')
    );
  }, [additionalInfo, fields, actions]);

  useFocusTrap(focusOnInput ? null : contentRef, false, [contentRef]);

  useFocusWithin([focusOnInput ? null : contentRef], focused =>
    contentRef.current
      ?.querySelectorAll(focusableSelector)
      .forEach(el => (focused ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')))
  );

  useEscape(() => containerRef.current?.focus(), focusOnInput ? null : containerRef, [
    containerRef
  ]);

  useContentHeight(contentRef, containerRef);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        containerRef.current === getActiveElement() &&
        (!focusOnInput || (focusOnInput && hasInteractiveElements > 1))
      ) {
        event.preventDefault();
        contentRef.current?.querySelectorAll<HTMLElement>(focusableSelector)[0]?.focus();
      }
      onKeyDown?.(event);
    },
    [containerRef, contentRef]
  );

  const imageContent = useMemo(() => {
    if (!image) return undefined;

    const innerContent = tile ? (
      <StyledTileContent
        foregroundColor={tile.foregroundColor}
        backgroundColor={tile.backgroundColor}
        size={tile.size}
      >
        {tile.icon ? <Icon name={tile.icon} /> : tile.text || <Icon name='picture' />}
      </StyledTileContent>
    ) : (
      <Flex
        container={{ justify: 'center', alignItems: 'center' }}
        as={StyledImageContainer}
        placement={image.placement}
        size={image.size}
      >
        {(isBrokenImage && resolvedImageSrc === prevImageSrc) || resolvedImageSrc === undefined ? (
          <Icon name='picture' aria-label={t('image_load_error')} />
        ) : (
          <StyledImage
            data-testid={testIds.image}
            {...image}
            onError={() => setIsBrokenImage(true)}
            onLoad={() => setIsBrokenImage(false)}
          />
        )}
      </Flex>
    );

    return (
      <Flex
        container={{ justify: 'center', alignItems: 'center' }}
        as={StyledImageCell}
        placement={image.placement}
        size={image.size}
        isTile={isTile}
        style={
          !isTile && image.src && !isBrokenImage ? { '--bg-image': `url(${image.src})` } : undefined
        }
      >
        {innerContent}
      </Flex>
    );
  }, [
    isBrokenImage,
    prevImageSrc,
    image?.placement,
    image?.size,
    tile?.icon,
    tile?.text,
    tile?.foregroundColor,
    tile?.backgroundColor
  ]);

  let asProp: GridProps['as'] = StyledSelectionCard;
  if (image) {
    asProp = (image.placement ?? 'inline-start').startsWith('block')
      ? StyledSelectionCardStacked
      : StyledSelectionCardInline;
  }

  const isFieldValueListItemProps = (
    item: FieldValueListItemProps | FieldValueItemPropsOmitName
  ): item is FieldValueListItemProps => {
    return hasProp(item, 'name');
  };

  return (
    <Grid
      data-testid={testIds.root}
      {...restProps}
      id={id}
      onClick={onClick}
      as={asProp}
      placement={image?.placement}
      size={image?.size}
      container={{ cols: '1fr', rows: 'auto' }}
      disabled={disabled}
      readOnly={readOnly}
      role={hasInteractiveElements ? 'group' : 'article'}
      aria-label={label}
      ref={containerRef}
      data-main-focus={focusOnInput ? undefined : true}
      tabIndex={focusOnInput ? undefined : 0}
      onKeyDown={handleKeyDown}
    >
      {image && image.placement !== 'inline-end' && imageContent}
      {/* element needed to get actual content height of the content container */}
      <StyledContentCell>
        <Flex
          as={StyledContentContainer}
          container={{ direction: 'column', gap: 0.75 }}
          ref={contentRef}
        >
          <Flex as={StyledHeader} container={{ alignItems: 'center', justify: 'between' }}>
            {isValidElement(heading) ? (
              cloneElement(heading, {
                ...{
                  tabIndex: focusOnInput ? undefined : -1,
                  additionalInfo,
                  'data-main-focus': focusOnInput ? true : undefined,
                  'aria-details': hasAdditionalContent ? contentId : undefined
                }
              })
            ) : (
              <span
                data-testid={testIds.label}
                aria-details={hasAdditionalContent ? contentId : undefined}
              >
                {heading}
              </span>
            )}
            {actions && (
              <SelectionCardActions
                data-testid={testIds.actions}
                actions={actions}
                disabled={disabled}
                contextualLabel={label}
              />
            )}
          </Flex>
          {hasAdditionalContent &&
            (fields.every(field => isFieldValueListItemProps(field)) ? (
              <FieldValueList id={contentId} data-testid={testIds.content} fields={fields} />
            ) : (
              <>
                {fields.length === 1 ? (
                  <SingleField
                    id={contentId}
                    data-testid={testIds.content}
                    truncate={fields[0].truncate}
                    value={fields[0].value}
                  />
                ) : (
                  <Flex
                    container={{ direction: 'column', gap: 1 }}
                    as={StyledValueList}
                    id={contentId}
                    data-testid={testIds.content}
                  >
                    {fields.map(field => (
                      <li key={field.id}>
                        {field.truncate ? (
                          <ShowMoreLess lines={3}>{field.value}</ShowMoreLess>
                        ) : (
                          field.value
                        )}
                      </li>
                    ))}
                  </Flex>
                )}
              </>
            ))}
        </Flex>
        {footer && <StyledCardFooter>{footer}</StyledCardFooter>}
      </StyledContentCell>
      {image?.placement === 'inline-end' && imageContent}
    </Grid>
  );
});

export default withTestIds(SelectionCard, getSelectionCardTestIds);
